import type { ReactNode } from "react";
import {
  Header,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  SubmenuTrigger,
} from "react-aria-components";

import { cn } from "@/shared/lib/cn";
import "./merryChoiceSurfaces.css";

export interface MerryDropdownMenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  detail?: ReactNode;
  destructive?: boolean;
  separated?: boolean;
  submenu?: readonly MerryDropdownMenuItem[];
}

export interface MerryDropdownMenuSection {
  id: string;
  title: string;
  items: readonly MerryDropdownMenuItem[];
}

interface MerryDropdownMenuProps {
  ariaLabel: string;
  children: ReactNode;
  className?: string;
  isOpen?: boolean;
  items?: readonly MerryDropdownMenuItem[];
  onAction: (id: string) => void;
  onOpenChange?: (open: boolean) => void;
  placement?: "bottom end" | "left top";
  sections?: readonly MerryDropdownMenuSection[];
}

function renderMenuItem(item: MerryDropdownMenuItem, onAction: (id: string) => void) {
  const trigger = (
    <MenuItem
      className={cn(
        "merry-dropdown-menu__item",
        item.destructive && "merry-dropdown-menu__item--destructive",
        item.separated && "merry-dropdown-menu__item--separated",
      )}
      id={item.id}
      key={item.id}
      textValue={item.label}
    >
      {item.icon}
      <span className="merry-dropdown-menu__label">{item.label}</span>
      {item.detail && <span className="merry-dropdown-menu__detail">{item.detail}</span>}
      {item.submenu && <span className="merry-dropdown-menu__chevron" aria-hidden="true">›</span>}
    </MenuItem>
  );

  if (!item.submenu) return trigger;

  return (
    <SubmenuTrigger key={item.id}>
      {trigger}
      <Popover className="merry-dropdown-menu merry-dropdown-menu--submenu" placement="left top">
        <Menu aria-label={`${item.label}选项`} onAction={(key) => onAction(String(key))}>
          {item.submenu.map((child) => renderMenuItem(child, onAction))}
        </Menu>
      </Popover>
    </SubmenuTrigger>
  );
}

/** Menu semantics and visual states shared by feature-owned dropdown actions. */
export function MerryDropdownMenu({
  ariaLabel,
  children,
  className,
  isOpen,
  items,
  onAction,
  onOpenChange,
  placement = "bottom end",
  sections,
}: MerryDropdownMenuProps) {
  return (
    <MenuTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      {children}
      <Popover className={cn("merry-dropdown-menu", className)} placement={placement}>
        <Menu aria-label={ariaLabel} onAction={(key) => onAction(String(key))}>
          {sections
            ? sections.map((section) => (
                <MenuSection key={section.id}>
                  <Header className="merry-dropdown-menu__heading">{section.title}</Header>
                  {section.items.map((item) => renderMenuItem(item, onAction))}
                </MenuSection>
              ))
            : items?.map((item) => renderMenuItem(item, onAction))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
